// Crazy Eights — client rules mirror (JS port of backend/crazy_eights.py).
// USED BY: App.js practice mode + the golden-transcript test at
// __tests__/c8_golden.test.js. Live sessions use the server-authoritative
// engine via WS.
//
// Any rule change MUST land in backend/crazy_eights.py FIRST, then be
// ported here and asserted by the golden transcript test.

export const C8_RANKS = ['2','3','4','5','6','7','8','9','10','J','Q','K','A'];
export const C8_SUIT_IDS = ['spades','hearts','diamonds','clubs'];
// SEP 8 2026 GEMS RULES: Aces add +4 to the draw stack, so the cap rises.
export const C8 = { DEAL_2P: 7, DEAL_MULTI: 5, MAX_STACK: 12, DRAW_LIMIT: 1 };

export const c8MakeDeck = () => {
  const d = [];
  for (const s of C8_SUIT_IDS) for (const r of C8_RANKS) d.push({ suit: s, rank: r });
  return d;
};

const c8Shuffle = (a) => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const c8NewGame = (playerIds) => {
  const deck = c8Shuffle(c8MakeDeck());
  const handSize = playerIds.length === 2 ? C8.DEAL_2P : C8.DEAL_MULTI;
  const hands = {};
  for (const pid of playerIds) {
    hands[pid] = [];
    for (let i = 0; i < handSize; i++) hands[pid].push(deck.pop());
  }
  let starter = deck.pop();
  while (starter.rank === '8') {
    deck.splice(Math.floor(Math.random() * deck.length), 0, starter);
    starter = deck.pop();
  }
  return {
    players: [...playerIds], hands, stock: deck, discard: [starter],
    active_suit: starter.suit, current_index: 0, direction: 1, pending_draw: 0,
    state: 'AWAITING_PLAY', winner: null,
    last_card_pending_pid: null, last_card_declared_pid: null,
  };
};

export const c8Current = (g) => g.players[g.current_index];

const c8Advance = (g, steps = 1) => {
  const n = g.players.length;
  g.current_index = ((g.current_index + g.direction * steps) % n + n) % n;
};

const c8DrawN = (g, n) => {
  const out = [];
  for (let i = 0; i < n; i++) {
    if (g.stock.length === 0) {
      if (g.discard.length <= 1) break;
      const top = g.discard.pop();
      g.stock = c8Shuffle(g.discard);
      g.discard = [top];
    }
    if (g.stock.length) out.push(g.stock.pop());
  }
  return out;
};

export const c8IsLegal = (g, card) => {
  if (g.pending_draw > 0) return card.rank === '2' || card.rank === 'A'; // GEMS rules: 2 (+2) or A (+4) answers a stack
  if (card.rank === '8') return true;
  const top = g.discard[g.discard.length - 1];
  return card.suit === g.active_suit || card.rank === top.rank;
};

export const c8PlayCard = (g, pid, idx, chosenSuit = null) => {
  if (g.state === 'GAME_OVER') return { ok: false, err: 'game over' };
  if (c8Current(g) !== pid) return { ok: false, err: 'not your turn' };
  const hand = g.hands[pid];
  if (!(idx >= 0 && idx < hand.length)) return { ok: false, err: 'bad index' };
  const card = hand[idx];
  if (!c8IsLegal(g, card)) return { ok: false, err: 'illegal card' };
  const winning = hand.length === 1;
  if (card.rank === '8' && !winning && !C8_SUIT_IDS.includes(chosenSuit)) {
    return { ok: false, err: 'must choose suit' };
  }
  hand.splice(idx, 1);
  g.discard.push(card);
  if (hand.length === 0) {
    g.winner = pid; g.state = 'GAME_OVER';
    g.active_suit = (card.rank === '8' && C8_SUIT_IDS.includes(chosenSuit)) ? chosenSuit : card.suit;
    g.last_card_pending_pid = null; g.last_card_declared_pid = null;
    return { ok: true };
  }
  g.active_suit = card.rank === '8' ? chosenSuit : card.suit;
  const r = card.rank;
  // SEP 8 2026 — OFFICIAL GEMS ACTION CARDS (matches backend crazy_eights.py):
  //   2 = draw 2 (stacks) · A = DRAW 4 (stacks) · J = SKIP · Q = REVERSE · 8 = wild
  if (r === '2') { g.pending_draw = Math.min(g.pending_draw + 2, C8.MAX_STACK); c8Advance(g, 1); }
  else if (r === 'A') { g.pending_draw = Math.min(g.pending_draw + 4, C8.MAX_STACK); c8Advance(g, 1); }
  else if (r === 'J') { c8Advance(g, 2); }
  else if (r === 'Q') {
    g.direction *= -1;
    if (g.players.length === 2) c8Advance(g, g.players.length); // 2p: extra turn
    else c8Advance(g, 1);
  }
  else { c8Advance(g, 1); }
  if (hand.length === 1) { g.last_card_pending_pid = pid; g.last_card_declared_pid = null; }
  g.state = 'AWAITING_PLAY';
  return { ok: true };
};

export const c8DrawCard = (g, pid) => {
  if (c8Current(g) !== pid) return { ok: false, err: 'not your turn' };
  if (g.pending_draw > 0) {
    const drew = c8DrawN(g, g.pending_draw); g.hands[pid].push(...drew);
    g.pending_draw = 0; c8Advance(g, 1); g.state = 'AWAITING_PLAY';
    return { ok: true, info: { drew: drew.length, penalty: true } };
  }
  const drew = c8DrawN(g, C8.DRAW_LIMIT); g.hands[pid].push(...drew);
  const card = drew[0] || null;
  if (card && c8IsLegal(g, card)) {
    g.state = 'AWAITING_PLAY_DRAWN';
    return { ok: true, info: { drew: 1, playable: true, card_index: g.hands[pid].length - 1 } };
  }
  c8Advance(g, 1); g.state = 'AWAITING_PLAY';
  return { ok: true, info: { drew: drew.length, playable: false } };
};

export const c8PassAfterDraw = (g, pid) => {
  if (g.state !== 'AWAITING_PLAY_DRAWN') return { ok: false, err: 'not in draw-choice state' };
  if (c8Current(g) !== pid) return { ok: false, err: 'not your turn' };
  c8Advance(g, 1); g.state = 'AWAITING_PLAY';
  return { ok: true };
};

export const c8DeclareLastCard = (g, pid) => {
  if (g.state === 'GAME_OVER') return { ok: false, err: 'game over' };
  const hand = g.hands[pid] || [];
  if (hand.length !== 1) return { ok: false, err: 'not down to one card' };
  if (g.last_card_pending_pid !== pid) return { ok: false, err: 'no last-card window open' };
  g.last_card_declared_pid = pid;
  return { ok: true };
};

// AI helpers (used by practice mode only) — kept here so App.js has a
// single import from c8_engine.
export const c8ChooseAISuit = (hand) => {
  const counts = { spades: 0, hearts: 0, diamonds: 0, clubs: 0 };
  for (const c of hand) if (c.rank !== '8') counts[c.suit]++;
  let best = 'spades', bestN = -1;
  for (const s of C8_SUIT_IDS) if (counts[s] > bestN) { best = s; bestN = counts[s]; }
  return best;
};

export const c8AIChoosePlay = (g, pid) => {
  const hand = g.hands[pid];
  const legal = hand.map((c, i) => ({ c, i })).filter(x => c8IsLegal(g, x.c));
  if (legal.length === 0) return { action: 'draw' };
  const isAction = (r) => r === '2' || r === '8' || r === 'Q' || r === 'A';
  const nonAction = legal.filter(x => !isAction(x.c.rank));
  const pick = (nonAction.length > 0 ? nonAction : legal)[0];
  return {
    action: 'play',
    card_index: pick.i,
    chosen_suit: pick.c.rank === '8' ? c8ChooseAISuit(hand.filter((_, i) => i !== pick.i)) : null,
  };
};

// Snapshot builder used by the golden-transcript test. Mirrors the shape
// produced by backend/crazy_eights.public_state (with viewer_pid=null),
// PLUS the full per-player hand map so exact equality is enforced.
export const c8Snapshot = (g) => ({
  players: [...g.players],
  hand_counts: Object.fromEntries(Object.entries(g.hands).map(([k, v]) => [k, v.length])),
  hands: Object.fromEntries(Object.entries(g.hands).map(([k, v]) => [k, v.map((c) => ({ suit: c.suit, rank: c.rank }))])),
  discard_top: g.discard.length ? { suit: g.discard[g.discard.length - 1].suit, rank: g.discard[g.discard.length - 1].rank } : null,
  active_suit: g.active_suit,
  stock_count: g.stock.length,
  current_player: c8Current(g),
  direction: g.direction,
  pending_draw: g.pending_draw,
  state: g.state,
  winner: g.winner,
  last_card_pending_pid: g.last_card_pending_pid,
  last_card_declared_pid: g.last_card_declared_pid,
});
